// Los enlaces de la casa: legales, dónde se escucha, redes y direcciones.
//
// 🔴 **Una sola lista, y esta.** Vivía dentro de `App.vue`, que la usaba para el
// pie y —recortada a cinco— para el panel móvil. El 18.09.26 la pidió también
// Contacto, y copiarla allí era la manera segura de que las dos se separaran:
// la página de Contacto anterior tenía `bsky.app/profile/astra.fm` y
// `open.spotify.com/user/astra.fm`, y el pie apunta a otras dos direcciones
// desde hace meses. Una de las dos estaba mal y nadie se enteró.
//
// Quien necesite un enlace del sitio lo pide aquí. Si mañana cambia una cuenta,
// cambia en un sitio y cambia en todos.

/**
 * Las columnas del pie, en su orden.
 *
 * `to` es una ruta de la casa y `href` un destino de fuera. Un ítem sin ninguno
 * de los dos es texto, que es como se pinta la frase de la emisora.
 *
 * `medir` es el evento de analítica que dispara el clic, cuando lo hay.
 */
export const COLUMNAS_PIE = [
  {
    title: "Legal",
    items: [
      { label: "Términos y condiciones", to: "/legal/terms" },
      { label: "Política de privacidad", to: "/legal/privacy" },
      { label: "Política de cookies", to: "/legal/cookies" },
      { label: "Contacto", to: "/legal/contact" },
    ],
  },
  {
    title: "Escuchar",
    items: [
      { label: "TuneIn", href: "https://tunein.com/radio/Astra-Riot-s188076/" },
      {
        label: "Spotify",
        href: "https://open.spotify.com/user/ultravoz?si=1d423266c41449ed",
      },
      {
        label: "App iOS",
        href: "https://apps.apple.com/app/astra-fm/id6748376717",
        medir: ["descarga_app", { plataforma: "ios" }],
      },
      {
        label: "App Android",
        href: "https://play.google.com/store/apps/details?id=fm.astra.app",
        medir: ["descarga_app", { plataforma: "android" }],
      },
    ],
  },
  {
    title: "Social",
    items: [
      {
        label: "Bluesky",
        href: "https://bsky.app/profile/astra-radio.bsky.social",
      },
      { label: "Instagram", href: "https://www.instagram.com/astralab.fm/" },
      { label: "Facebook", href: "https://www.facebook.com/AstraRadioLab" },
      // Sin el `?si=` con el que YouTube reparte los enlaces: es un
      // identificador de sesión de quien comparte, no hace falta para llegar
      // al canal y de paso atribuye cada visita a un compartido que no es este.
      { label: "YouTube", href: "https://www.youtube.com/@fmastra" },
    ],
  },
  {
    title: "Contacto",
    items: [
      { label: "soporte@astra.fm", href: "mailto:soporte@astra.fm" },
      { label: "info@astra.fm", href: "mailto:info@astra.fm" },
      // ⚠️ **A Radar entero, sin ancla.** Iba a `/radar#mandar` y **ese ancla no
      // existe en ninguna parte**: lo escribí yo al montar el pie y nunca hubo
      // un `id="mandar"` al que llegar, así que el enlace dejaba a la gente
      // arriba de Radar sin que se notara que había fallado. Pablo, 21.09.26:
      // «no uses un ancla porque no tiene sentido, el label dice Manda tu
      // material, deberá ir a radar».
      { label: "Manda tu material", to: "/radar" },
      // 🔴 Aquí iba «Colabora», y **se retira el 18.09.26 por encargo de Pablo**:
      // «no es necesario». Nunca tuvo página propia —apuntaba a `/legal/contact`,
      // igual que la celda «Contacto» de la columna Legal—, así que era la misma
      // página dicha dos veces en el mismo pie. Lo que prometía está en Contacto,
      // en «Colaboraciones».
    ],
  },
  {
    title: "Emisión",
    items: [
      {
        label:
          "Astra FM es una radio independiente dedicada a la música alternativa. Una selección de sonidos por descubrir y canciones que permanecen, las 24 horas.",
      },
    ],
  },
];

/** Los ítems de una columna del pie, por su título. */
function columna(titulo) {
  return COLUMNAS_PIE.find((c) => c.title === titulo)?.items || [];
}

export const REDES = columna("Social");
export const ESCUCHAR = columna("Escuchar");

/**
 * El nombre de la cuenta tal y como va en la dirección: «@fmastra»,
 * «astralab.fm», «AstraRadioLab».
 *
 * Sale del último tramo de la URL, que es donde lo escriben las cuatro redes, y
 * **solo si parece un nombre de cuenta**. Si mañana una de ellas cambia la forma
 * de sus enlaces, esto devuelve cadena vacía y quien lo pinte se queda sin el
 * dato, en vez de enseñar un trozo de ruta.
 */
export function cuentaDe(href) {
  try {
    const tramos = new URL(href).pathname.split("/").filter(Boolean);
    const ultimo = tramos[tramos.length - 1] || "";
    return /^[@\w.-]{2,40}$/.test(ultimo) ? ultimo : "";
  } catch {
    return "";
  }
}
